// Omada同期 tab — pending command monitor + release history log.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ArrowsClockwise, ArrowCounterClockwise, ClockCounterClockwise, CheckCircle, WarningCircle, Hourglass } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { itApi } from '../itApi';
import { formatShort } from '@/utils/datetime';

const OP_LABELS = {
  rename: '名前変更', fixed_ip: '固定IP設定', clear_fixed_ip: '固定IP解除',
  net_update: 'VLAN更新', net_create: 'VLAN作成',
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return formatShort(iso);
  } catch { return '—'; }
};

const cmdDetail = (c) => c.detail || (c.op === 'rename' ? c.name : c.op === 'fixed_ip' ? c.fixed_ip : '—') || '—';

const CmdStatusBadge = ({ status }) => {
  if (status === 'pending') return <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">反映待ち</Badge>;
  if (status === 'done') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">反映済み</Badge>;
  return <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100">失敗</Badge>;
};

export const OmadaSyncTab = ({ commands, cmdPending, onRefresh }) => {
  const stats = useMemo(() => {
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const recent = commands.filter((c) => { const t = new Date(c.created_at || 0).getTime(); return Number.isFinite(t) && t >= cutoff; });
    return {
      pending: recent.filter((c) => c.status === 'pending').length,
      done: recent.filter((c) => c.status === 'done').length,
      failed: recent.filter((c) => c.status === 'failed').length,
    };
  }, [commands]);

  return (
  <div className="space-y-4">
  {/* 24h stat strip — at-a-glance whether reservations are actually applying */}
  <div className="grid grid-cols-3 gap-3" data-testid="omada-stats-strip">
    <StatCard icon={<Hourglass size={16} weight="fill" />} label="反映待ち (24h)" value={stats.pending} tone="amber" testid="omada-stat-pending" />
    <StatCard icon={<CheckCircle size={16} weight="fill" />} label="反映済み (24h)" value={stats.done} tone="emerald" testid="omada-stat-done" />
    <StatCard icon={<WarningCircle size={16} weight="fill" />} label="失敗 (24h)" value={stats.failed} tone="rose" testid="omada-stat-failed" />
  </div>

  <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="omada-sync-status">
    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-zinc-800">Omada反映状況</span>
        {cmdPending > 0
          ? <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100" data-testid="omada-pending-badge">反映待ち {cmdPending}件</Badge>
          : <Badge variant="outline" className="text-zinc-500" data-testid="omada-pending-badge">反映待ちなし</Badge>}
      </div>
      <button type="button" data-testid="omada-monitor-refresh" onClick={onRefresh} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-teal-700 px-2 py-1 rounded-md hover:bg-blue-50">
        <ArrowsClockwise size={14} /> 更新
      </button>
    </div>
    {commands.length === 0 ? (
      <p className="px-4 py-10 text-center text-sm text-zinc-400" data-testid="omada-no-commands">反映コマンドの履歴はありません</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm" data-testid="omada-command-table">
          <thead>
            <tr className="bg-slate-50 text-zinc-500 text-xs">
              {['操作', '対象', '内容', '状態', '予約者', '予約日時', '反映日時'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2.5">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {commands.map((c) => (
              <tr key={c.id} className={`border-t border-slate-50 hover:bg-slate-50/60 ${c.status === 'failed' ? 'bg-rose-50/40' : ''}`}>
                <td className="px-4 py-2.5 text-zinc-700">{OP_LABELS[c.op] || c.op}</td>
                <td className="px-4 py-2.5 font-mono text-zinc-800">{c.ip_label || '—'}</td>
                <td className="px-4 py-2.5 text-zinc-600">{cmdDetail(c)}</td>
                <td className="px-4 py-2.5">
                  <CmdStatusBadge status={c.status} />
                  {c.status === 'failed' && c.error && <span className="block text-[11px] text-red-500 mt-1 max-w-[200px] truncate" title={c.error}>{c.error}</span>}
                </td>
                <td className="px-4 py-2.5 text-zinc-500">{c.created_by || '—'}</td>
                <td className="px-4 py-2.5 text-zinc-500 tabular-nums">{fmtTime(c.created_at)}</td>
                <td className="px-4 py-2.5 text-zinc-500 tabular-nums">{fmtTime(c.applied_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    <p className="px-4 py-2 text-[11px] text-zinc-400 border-t border-slate-50">※ 反映はルーター側エージェントの次回同期時（最大10分間隔）に実行されます。</p>
  </div>

  <WronglyReleasedCard />
  <ReleaseLogCard />
  </div>
  );
};

const TONE_CLS = {
  amber: 'text-amber-600 bg-amber-50 border-amber-100',
  emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100',
  rose: 'text-rose-600 bg-rose-50 border-rose-100',
};
function StatCard({ icon, label, value, tone, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 flex items-center gap-3" data-testid={testid}>
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border ${TONE_CLS[tone]}`}>{icon}</span>
      <div className="min-w-0">
        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">{label}</p>
        <p className="text-lg font-bold text-zinc-800 tabular-nums leading-tight">{value}</p>
      </div>
    </div>
  );
}

// 誤って解放された可能性のある行（ネットワーク機器など）— 洗い出して戻す
function WronglyReleasedCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    itApi.omadaWronglyReleased().then(setData).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const restoreAll = async () => {
    setBusy(true);
    try {
      const dry = await itApi.omadaRestoreWrongly(true);
      if (!dry.would_restore) { toast.info('戻せる行がありません'); return; }
      const names = dry.rows.map((r) => `${r.ip_address} ${r.device_name || ''}`).join('\n');
      if (!window.confirm(`${dry.would_restore}件を元に戻します。\n\n${names}`)) return;
      const out = await itApi.omadaRestoreWrongly(false);
      toast.success(`${out.restored}件を元に戻しました（以降は自動解放されません）`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '復元に失敗しました');
    } finally { setBusy(false); }
  };

  if (!data || !data.count) return null;
  return (
    <div className="bg-amber-50/70 rounded-lg border border-amber-200 p-4" data-testid="wrongly-released-card">
      <p className="text-sm font-semibold text-amber-800">
        誤って解放された可能性のある行 {data.count}件
      </p>
      <p className="text-[11px] text-amber-700 mt-0.5">
        ネットワーク機器（スイッチ・AP）や用途がスイッチ＆WIFIの行です。{data.note_ja}
      </p>
      <ul className="mt-2 space-y-0.5">
        {data.rows.slice(0, 20).map((r) => (
          <li key={r.id} className="text-xs text-zinc-700" data-testid={`wrongly-released-${r.id}`}>
            <span className="font-mono">{r.ip_address}</span> {r.device_name || '—'}
            <span className="text-zinc-500">（{r.why_ja}）</span>
            {!r.can_restore && <span className="text-rose-600 ml-1">復元不可（別の用途で使用中）</span>}
          </li>
        ))}
      </ul>
      <Button size="sm" disabled={busy} onClick={restoreAll} data-testid="wrongly-released-restore-all"
        className="mt-2 h-8 text-xs bg-amber-700 hover:bg-amber-800">
        まとめて元に戻す
      </Button>
    </div>
  );
}

// 自動解放履歴 — 30日+未確認で解放されたIPの一覧と1クリック復元
function ReleaseLogCard() {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    itApi.omadaReleaseLog().then(setLogs).catch(() => setLogs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (log) => {
    setBusy(log.id);
    try {
      await itApi.omadaRestoreRelease(log.id);
      toast.success(`${log.ip_address} を復元しました`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '復元に失敗しました');
    } finally { setBusy(null); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="omada-release-log">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={16} weight="duotone" className="text-amber-600" />
          <span className="text-sm font-semibold text-zinc-800">自動解放履歴</span>
          {logs.length > 0 && <Badge variant="outline" className="text-zinc-500">{logs.length}件</Badge>}
        </div>
        <button type="button" data-testid="release-log-refresh" onClick={load} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-teal-700 px-2 py-1 rounded-md hover:bg-blue-50">
          <ArrowsClockwise size={14} /> 更新
        </button>
      </div>
      {logs.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-zinc-400" data-testid="release-log-empty">自動解放されたIPはありません</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="release-log-table">
            <thead>
              <tr className="bg-slate-50 text-zinc-500 text-xs">
                {['IPアドレス', 'デバイス名', '使用者', 'MACアドレス', '理由', '解放日時', '状態', ''].map((h, i) => <th key={i} className="text-left font-semibold px-4 py-2.5">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-slate-50 hover:bg-slate-50/60" data-testid={`release-log-row-${l.id}`}>
                  <td className="px-4 py-2.5 font-mono text-zinc-800">{l.ip_address}</td>
                  <td className="px-4 py-2.5 text-zinc-700">{l.device_name || '—'}</td>
                  <td className="px-4 py-2.5 text-zinc-600">{l.user || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{l.mac_address || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-zinc-500" data-testid={`release-log-cause-${l.id}`}>
                    {l.cause_label_ja || '30日以上未確認'}
                  </td>
                  <td className="px-4 py-2.5 text-zinc-500 tabular-nums">{fmtTime(l.released_at)}</td>
                  <td className="px-4 py-2.5">
                    {l.restored_at ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" title={`${l.restored_by || ''} ${fmtTime(l.restored_at)}`}>復元済み</Badge>
                    ) : l.can_restore ? (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">解放済み</Badge>
                    ) : (
                      <Badge variant="outline" className="text-zinc-400" title="このIPは現在別の用途で使用中です">復元不可</Badge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {l.can_restore && (
                      <button type="button" disabled={busy === l.id} onClick={() => restore(l)}
                        data-testid={`release-log-restore-${l.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold text-teal-700 border border-blue-200 hover:bg-blue-50 transition-colors disabled:opacity-50">
                        <ArrowCounterClockwise size={12} weight="bold" /> 復元
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="px-4 py-2 text-[11px] text-zinc-400 border-t border-slate-50">※ 30日以上Omadaで確認されなかったIPは自動解放されます（手入力の行・ネットワーク機器・固定の行・DHCP予約のある行は対象外）。DHCPでIPが変わった端末は、古いIPをその場で解放します。復元すると生存扱いとなり、次の30日カウントが始まります。</p>
    </div>
  );
}
